import * as a from '@mitranim/js/all.mjs'
import * as u from './util.mjs'
import * as c from './conf.mjs'
import * as d from './dat.mjs'
import * as ct from './ctx.mjs'

/*
Represents the main object in our system, the root of the object hierarchy.
We create one instance of this class in the browser, and one instance for
each HTTP request on the server.

Also see subclasses `ClientEnv` and `SrvEnv`.

Implementation note. It's important for this object to NOT be observable.
Because this is the root object, much of our code accesses its properties.
When it's observable, many view components subscribe to it accidentally,
even when they don't need to. This wouldn't be a problem if updates of `env`
properties were very rare. However, we update its properties on each page
transition. When `env` is observable, this causes many view components to
perform an update when we start the transition, which is completely redundant.
*/
export class Env extends a.Emp {
  constructor({baseUrl, headers}) {
    super()

    /*
    This base URL provides the default origin for our HTTP requests in SSR.
    In browsers, this is unnecessary, as HTTP requests can use paths such as
    `/api/blah`, rather than full URLs such as `scheme://domain/api/blah`,
    because the browser provides the base origin. In SSR, HTTP requests must
    use full URLs, and this field provides the base origin.
    */
    this.baseUrl = a.toInstOpt(baseUrl, u.Url)
    this.headers = a.toInst(headers, Headers)
    this.ctx = a.optInst(undefined, ct.Ctx)
    this.pending = a.optInst(undefined, ct.Ctx)

    // Used for SSR-to-client data translation.
    this.transferData = false
  }

  static {a.memGet(this)}
  get dat() {return new d.Dat(this.ctx)}
  get cook() {throw u.errImplementInSubclass()}
  get disk() {return new u.CookieDisk(this.cook)}
  get ana() {return new u.Ana(this.ctx)} // Nop dummy for SSR.
  get filestack() {return new u.Filestack()} // Nop dummy for SSR.

  get langs() {
    return new u.ClientLangs()
      .add(this.cook.getVal(c.COOKIE_NAME_LANG))
      .addFrom(this.navigatorLangs())
      .parse(this.acceptLanguage())
      .sorted()
  }

  get lang() {return this.langs.viable()}

  /*
  In SSR, we should NOT use `globalThis.navigator.languages`.
  This should only be filled-in on the client.
  */
  navigatorLangs() {}

  setCurrent(ctx) {
    const prevCtx = this.ctx
    const prevPend = this.pending

    this.ctx = a.optInst(ctx, ct.Ctx)
    this.pending = undefined

    if (prevCtx !== ctx) prevCtx?.deinit()
    if (prevPend !== ctx) prevPend?.deinit()
  }

  setPending(ctx) {
    const prev = this.pending
    this.pending = a.optInst(ctx, ct.Ctx)
    if (prev !== ctx) prev?.deinit()
  }

  unsetPending(ctx) {if (ctx === this.pending) this.setPending()}
  getSessionId() {return this.cook.getVal(c.COOKIE_NAME_SESSION)}
  userAgent() {return this.headers.get(`user-agent`)}
  acceptLanguage() {this.headers.get(`accept-language`)}
  pageRequiresData() {this.transferData = true}
  deinit() {this.setCurrent()}
}
